import React from "react";
import "./ProblemSection.css";
import DemoTour from "./DemoTour";

function generateTopDownTreeMarkdown(arrayStr) {
  try {
    const arr = JSON.parse(arrayStr);
    if (!Array.isArray(arr) || arr.length === 0) return "";

    const buildTree = (i) => {
      if (i >= arr.length || arr[i] == null) return null;
      return {
        val: arr[i],
        left: buildTree(2 * i + 1),
        right: buildTree(2 * i + 2),
      };
    };

    const root = buildTree(0);

    const getHeight = (node) => {
      if (!node) return 0;
      return 1 + Math.max(getHeight(node.left), getHeight(node.right));
    };

    const height = getHeight(root);
    const width = Math.pow(2, height) * 2;
    const canvas = Array(height * 2 - 1)
      .fill(null)
      .map(() => Array(width).fill(" "));

    const draw = (node, row, col, offset) => {
      if (!node) return;
      const valStr = String(node.val);
      for (let i = 0; i < valStr.length; i++) {
        canvas[row][col + i] = valStr[i];
      }

      if (node.left) {
        canvas[row + 1][col - offset] = "/";
        draw(node.left, row + 2, col - offset * 2, offset >> 1);
      }

      if (node.right) {
        canvas[row + 1][col + offset + valStr.length - 1] = "\\";
        draw(node.right, row + 2, col + offset * 2, offset >> 1);
      }
    };

    draw(root, 0, Math.floor(width / 2), Math.floor(width / 8));

    return canvas.map((row) => row.join("")).join("\n");
  } catch {
    return "";
  }
}

const ProblemSection = ({ problem, level }) => {
  if (!problem) return null;

  function formatInputs(inputVars, inputs) {
    if (!Array.isArray(inputs) || !Array.isArray(inputVars))
      return inputs ?? "N/A";

    return inputs
      .map((input, idx) => {
        const varName = inputVars[idx]?.name || `param${idx + 1}`;
        return `${varName} = ${input}`;
      })
      .join(", ");
  }

  return (
    <div className="new-problem-section">
      <div className="problem-metadata">
        <span className={`difficulty ${problem.difficulty?.toLowerCase()}`}>
          {problem.difficulty}
        </span>
        <h2 className="new-problem-title">{problem.title}</h2>
      </div>
      <p className="new-problem-level">{level} Level</p>
      <p className="new-problem-description">{problem.description}</p>

      {/* Ensure testCases is an array before mapping or accessing indices */}
      {Array.isArray(problem.testCases) && problem.testCases.length > 0 && (
        <div className="new-examples-section">
          {problem.testCases[0] && (
            <div className="new-example-card">
              <p className="new-example-title">Example 1:</p>
              <p className="new-example-content">
                Input:{" "}
                {formatInputs(problem.inputVars, problem.testCases[0]?.inputs)}
              </p>
              {problem.inputVars?.[0]?.type === "TreeNode" &&
                typeof problem.testCases[0]?.inputs?.[0] === "string" && (
                  <pre className="ascii-tree">
                    {generateTopDownTreeMarkdown(
                      problem.testCases[0]?.inputs?.[0]
                    )}
                  </pre>
                )}

              <p className="new-example-content">
                Output: {problem.testCases[0]?.expectedOutput ?? "N/A"}
              </p>
              {problem.testCases[0]?.explanation && (
                <p className="new-example-explanation">
                  Explanation: {problem.testCases[0].explanation}
                </p>
              )}
            </div>
          )}
          <br />
          {problem.testCases[1] && (
            <div className="new-example-card">
              <p className="new-example-title">Example 2:</p>
              <p className="new-example-content">
                Input:{" "}
                {formatInputs(problem.inputVars, problem.testCases[1]?.inputs)}
              </p>
              {problem.inputVars?.[0]?.type === "TreeNode" &&
                typeof problem.testCases[1]?.inputs?.[0] === "string" && (
                  <pre className="ascii-tree">
                    {generateTopDownTreeMarkdown(
                      problem.testCases[1]?.inputs?.[0]
                    )}
                  </pre>
                )}
              <p className="new-example-content">
                Output: {problem.testCases[1]?.expectedOutput ?? "N/A"}
              </p>
              {problem.testCases[1]?.explanation && (
                <p className="new-example-explanation">
                  Explanation: {problem.testCases[1].explanation}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {Array.isArray(problem.constraints) && problem.constraints.length > 0 && (
        <div className="new-constraints-section">
          <h3>Constraints</h3>
          <ul className="new-constraints-list">
            {problem.constraints.map((constraint, idx) => (
              <li key={idx} className="new-constraint-item">
                {constraint}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="problem-copyright">
        © 2025 BlockCode. All rights reserved.
      </div>

      {problem?.id === 476 && <DemoTour />}
    </div>
  );
};

export default ProblemSection;
